<template>
  <div class="asc-mx-rec-details-followup">
    <asc-card class="asc-mx-rec-details-followup__card" no-spacing flat>
      <asc-tab v-model="currentTab">
        <asc-tab-title title="MAINTENANCE RECOMMENDATION" />
        <asc-tab-title
          v-if="!isChronicRepeaters"
          title="MAINTENANCE ACTION FOLLOW-UP"
        />
      </asc-tab>
      <asc-tab-body :value="currentTab">
        <asc-tab-item>
          <v-row class="asc-maint-recom mt-0 pt-4" md="12" sm="12">
            <v-col v-if="!isChronicRepeaters" class="pt-0" md="8" sm="5">
              <p class="asc-Title">RECOMMENDATION</p>
              <asc-rich-text-input
                v-if="getRecommendationText"
                :key="getRecommendationText"
                :class="[
                  disabled
                    ? 'asc-mxrec-details-followup--disabled'
                    : 'asc-mxrec-details-followup',
                ]"
                :comment="getRecommendationText"
                :can-edit="false"
                hidden-menu
              >
              </asc-rich-text-input>
            </v-col>
            <v-col v-else class="pt-0" md="8" sm="5">
              <template v-if="isChronicRepeaters && repeaterId">
                <asc-plotly
                  :plotly-data="mappedPlotlyData"
                  :chronic-repeaters-data="{
                    pagename: 'final_fixes',
                    repeaterId: repeaterId,
                  }"
                  :is-readonly="isIssueReader || lockEnabled"
                />
              </template>
            </v-col>
            <v-col class="asc-Documents pt-0" md="4" sm="7">
              <p class="asc-Title">DOCUMENTS</p>
              <asc-file-upload-input
                v-model="attachments"
                :accepted-file-properties="fileTypeProperties"
                :allow-file-upload="
                  !isReadOnlyPermissionInvestigate || isCustomAlert
                "
                :on-upload-file="handleAttachmentsUploadFile"
                :on-remove-file="handleAttachmentsRemoval"
                :on-download-file="handleDownloadFile"
                :disabled="disabled"
                :readonly="
                  (isReadOnlyPermissionInvestigate && !isCustomAlert) ||
                  lockEnabled
                "
                :allow-file-remove="
                  !isReadOnlyPermissionInvestigate || isCustomAlert
                "
                :upload-error="mxActionAttachmentUploadError"
                viewable
                class="asc-mxrec-followup--attachments"
              ></asc-file-upload-input>
            </v-col>
          </v-row>
        </asc-tab-item>
        <asc-tab-item v-if="!isChronicRepeaters">
          <asc-mx-rec-follow-up-wrapper
            :is-recommendation-tab="isRecommendationTab"
            :data="data"
            :disabled="disabled"
            :is-custom-alert="isCustomAlert"
            :lock-enabled="lockEnabled"
            @updateFollowUpFields="updateFollowUpFields"
            @followUpDataChanges="handleFollowUpDataChanges"
          >
          </asc-mx-rec-follow-up-wrapper>
        </asc-tab-item>
      </asc-tab-body>
    </asc-card>
  </div>
</template>

<script>
import AscCard from '@/components/atoms/AscCard'
import AscTab from '@/components/atoms/AscTab'
import AscTabTitle from '@/components/atoms/AscTabTitle'
import AscTabBody from '@/components/atoms/AscTabBody'
import AscTabItem from '@/components/atoms/AscTabItem'
import AscRichTextInput from '@/components/atoms/AscRichTextInput'
import AscFileUploadInput from '@/components/molecules/AscFileUploadInput'
import AscPlotly from '../molecules/AscPlotly.vue'

import AscMxRecFollowUpWrapper from '../organisms/AscMxRecFollowUpWrapper.vue'
import {
  AscMxRecPropsMixin,
  AscMxRecPermissionMixin,
} from '@/components/mixins'

export default {
  name: 'AscMxRecDetailsFollowupWrapper',

  components: {
    AscCard,
    AscTab,
    AscTabTitle,
    AscTabBody,
    AscTabItem,
    AscRichTextInput,
    AscFileUploadInput,
    AscMxRecFollowUpWrapper,
    AscPlotly,
  },

  mixins: [AscMxRecPropsMixin, AscMxRecPermissionMixin],

  props: {
    fields: {
      type: Object,
      default: () => {},
    },
    files: {
      type: Array,
      default: () => [],
    },
    fileTypeProperties: {
      type: Object,
      default: () => {},
    },
    mxActionAttachmentUploadError: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => {},
    },
    lockEnabled: {
      type: Boolean,
      default: false,
    },
    plotlyData: {
      type: Object,
      default: () => {},
    },
    repeaterId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      maintenanceAttachments: [],
      currentTab: 0,
      attachments: this.files,
    }
  },

  computed: {
    getRecommendationText() {
      return this.fields.mxRecDetails
    },
    mappedPlotlyData() {
      if (this.isChronicRepeaters && this.plotlyData['ParetoBarChart']) {
        return this.plotlyData['ParetoBarChart']
      }
      return null
    },
  },

  watch: {
    files(newValue) {
      this.attachments = newValue
      if (newValue.length) {
        this.selectedRows = 0
        // eslint-disable-next-line
        newValue.map(fileObj => {
          if (fileObj?.isExternallyVisible) {
            this.selectedRows++
          }
        })
        this.$nextTick(() => {
          this.maintenanceAttachments = newValue.filter(file =>
            file?.filename?.match('MaintIssue'),
          )
        })
      }
    },
    mxActionAttachmentUploadError(newValue) {
      if (newValue) {
        this.maintenanceAttachments = this.maintenanceAttachments.filter(
          item => item.displayName !== newValue[0]?.name,
        )
      }
    },
  },

  methods: {
    handleAttachmentsUploadFile(file) {
      this.$emit('fileUploaded', {
        attachmentTypeId: this.fileTypeProperties.id,
        fileUpload: file,
        isExternallyVisible: true,
      })
    },
    handleAttachmentsRemoval(file) {
      this.maintenanceAttachments = this.maintenanceAttachments.filter(
        att => att.displayName !== file.displayName,
      )
      this.handleRemoveFile(file)
    },
    handleDownloadFile(fileToDownload) {
      this.$emit('downloadFile', fileToDownload)
    },
    updateFollowUpFields(fields) {
      this.$emit('updateFollowUpFields', fields)
    },
    handleFollowUpDataChanges() {
      this.$emit('followUpDataChanges', true)
    },
    handleRemoveFile(file) {
      if (Number.isInteger(file?.id)) {
        this.$emit('fileSelected', {
          remove: true,
          id: file.id,
        })
      } else {
        this.$emit('fileSelected', {
          cancel: true,
          file,
        })
      }
    },
  },
}
</script>

<style lang="scss">
.asc-Documents {
  border-left: 1px solid #d3d3d3;
}

p.asc-Title {
  font-size: 12px;
  line-height: 20px;
  font-weight: 400;
  text-transform: uppercase;
  font-size: normal;
  color: $gray-700;
}

.asc-mx-rec-details-followup {
  &__card {
    .asc-card__text {
      .asc-tab__content--primary {
        a {
          color: $accent-700;
          text-decoration: none;
          border-right-style: none;
          .v-icon {
            color: inherit;
          }
        }
      }
    }
  }

  &--disabled {
    cursor: not-allowed;
    width: 100px;
  }
}
</style>
